/**
 * @file: useServiceRecordMedias.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { ResourceAccessScope } from '@euler/model/enum';
import { MediaSourceType } from '@euler/model/report';
import { VehicleMediaSearchParams } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { useMemo } from 'react';

export type ServiceRecordMediaTarget =
  | {
      type: 'mileage';
      mileage: number;
    }
  | {
      type: 'year-month';
      year: number;
      month: number;
    };

export type VehicleMediaQueryParams = {
  origin: string;
  current?: boolean;
  licensePlateNo: string;
  vin: string;
  sources?: MediaSourceType[];
  scope?: ResourceAccessScope;
  target: ServiceRecordMediaTarget;
};

export const getVehicleMediaSearchParamsFromQueryParams = (
  args: VehicleMediaQueryParams,
): VehicleMediaSearchParams => {
  const { target } = args;
  const params: VehicleMediaSearchParams = {
    licensePlateNo: args.licensePlateNo,
    vin: args.vin,
    sources: args.sources,
    scope: args.scope,
  };
  if (target.type === 'mileage') {
    params.mileage = target.mileage;
  } else if (target.type === 'year-month') {
    params.year = target.year;
    params.month = target.month;
  }
  return params;
};

export const useServiceRecordMedias = (args: VehicleMediaQueryParams) => {
  const { origin, current, target } = args;

  const cachePath = useMemo(
    () =>
      target.type === 'mileage'
        ? `${args.licensePlateNo}/${origin}/medias/${target.mileage}.json`
        : `${args.licensePlateNo}/${origin}/medias/${target.year}-${target.month}.json`,
    [origin, args.licensePlateNo, target],
  );

  const params = getVehicleMediaSearchParamsFromQueryParams(args);

  const { serviceRecordService, vehicleInfoService } = useServiceFactory();

  return useCachedQuery(cachePath, current ? 0 : 24 * 3600, () =>
    current
      ? vehicleInfoService.searchMedias(params)
      : serviceRecordService.searchMedias(params),
  );
};
